import { AxiosStatusCode } from 'smg/axios';
import { SetBootstrapVersion } from 'smg/bootstrap';
import * as accounts from 'smg/customer-api/accounts';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as paypal from 'smg/customer-api/paypal';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { BindFancybox } from 'smg/fancybox';
import * as html from 'smg/html';
import { SetIconLibrary } from 'smg/icons';
import * as meta from 'smg/meta';
import * as modals from 'smg/modal';
import QueryString from 'smg/querystring';
import * as carts from './carts';

import 'smg/growthbook';

import './featured-products';
import './nav';

declare interface myWindow extends Window {
    metaPixel?: string;
}
declare const window: myWindow;

SetIconLibrary('bootstrapIcons');
SetBootstrapVersion(5);

modals.SetDefaults({
    appendButtonClass: 'text-white',
    // animation: true,
});

errors.Defaults.showErrorFunc = msg => new modals.default({
    body: html.FromString(`<div>
            <i class="bi bi-exclamation-circle-fill w-100 text-center mb-5 text-danger" style="font-size: 5em"></i>
            <div class="fs-4 text-center">${html.Escape(msg)}</div>
        </div>`),
    cancelText: 'Close',
});

BindFancybox();

// Token the last paintSession ran under — used by logout + storage-event
// guards so a cross-tab account swap repaints instead of logging out the
// wrong user or acting on a stale session response.
let currentToken: string | null = null;
// User id painted under currentToken — logout removes this entry from the
// stored account list. Set only on the logged-in path.
let currentUserID: string | null = null;

function clearInjectedAccounts() {
    document.querySelectorAll('.nav-account-injected').forEach(el => el.remove());
    document.querySelectorAll('.nav-accounts-header').forEach(el => el.classList.add('d-none'));
}

function paintSession() {
    const $navLogin = document.querySelectorAll('.nav-login');
    const $loggedIn = document.querySelectorAll('.loggedIn');

    // Captured before the session request so the account entry records the
    // token this response actually belongs to — another tab (or a fast
    // "Add account" login) can swap the live token mid-request.
    const loginToken = session.GetLoginToken();

    if (loginToken) {
        session.Session().then(sess => {
            // The live token moved while this request was in flight (another
            // tab or a fast "Add account" login) — this response describes
            // the OLD account, so don't let it paint the header. Repaint
            // under the current token instead of a full reload.
            if (session.GetLoginToken() !== loginToken) {
                paintSession();
                return;
            }

            // Token and user id become current together, only once the
            // session resolves. Setting the token earlier would leave a
            // window pairing the NEW token with the OLD user id, where
            // logout deletes the new account's session while evicting the
            // old account from the stored list.
            currentToken = loginToken;
            currentUserID = sess.user.id;

            document.querySelectorAll('.nav-name').forEach(el => {
                el.textContent = sess.user.fullName() || sess.user.company || 'My Account';
            });

            $navLogin.forEach(el => el.classList.add('d-none'));
            $loggedIn.forEach(el => el.classList.remove('d-none'));

            accounts.Remember({
                token: loginToken,
                userID: sess.user.id,
                name: sess.user.fullName() || sess.user.company || sess.user.emailAddress.email,
                email: sess.user.emailAddress.email,
            });

            renderAccountSwitcher();
        }).catch(err => {
            $navLogin.forEach(el => el.classList.remove('d-none'));
            throw err;
        });
    } else {
        currentToken = null;
        currentUserID = null;
        $navLogin.forEach(el => el.classList.remove('d-none'));
        $loggedIn.forEach(el => el.classList.add('d-none'));
        clearInjectedAccounts();
    }
}

function renderAccountSwitcher() {
    // Idempotent re-render: drop prior injections before re-building so a
    // cross-tab repaint doesn't stack duplicate rows.
    clearInjectedAccounts();

    const others = accounts.List().filter(account => !accounts.IsCurrent(account));
    if (!others.length) {
        return;
    }

    // One lock for the whole page: rapid clicks on two DIFFERENT accounts
    // would otherwise race two switch requests, letting response order (not
    // the user's final click) decide the active account.
    let switching = false;
    document.querySelectorAll('.nav-add-account').forEach($addAccount => {
        const $template = $addAccount.parentElement?.querySelector(':scope > template.nav-account-item') as HTMLTemplateElement | null;
        if (!$template) {
            return;
        }

        for (const account of others) {
            const $fragment = $template.content.cloneNode(true) as DocumentFragment;
            const $item = $fragment.firstElementChild as HTMLElement;
            $item.classList.add('nav-account-injected');
            ($item.querySelector('.account-name') as HTMLElement).textContent = account.name;
            ($item.querySelector('.account-email') as HTMLElement).textContent = account.email;
            ($item.querySelector('a') as HTMLAnchorElement).addEventListener('click', e => {
                e.preventDefault();
                if (switching) {
                    return;
                }
                switching = true;
                void accounts.Switch(account).then(() => {
                    window.location.reload();
                }).catch((err: unknown) => {
                    // Switch only evicts the stored account on 404 (dead/
                    // rotated token); a transient failure keeps the entry so
                    // it stays clickable.
                    switching = false;
                    // A dead token (404) evicts the account — the row
                    // vanishing IS the feedback, so an error modal on top of
                    // it would undo the graceful part of graceful eviction.
                    // Switch already dropped it from storage; re-rendering
                    // removes its rows from every menu at once.
                    if (AxiosStatusCode(err) === 404) {
                        renderAccountSwitcher();
                        return;
                    }
                    errors.ShowError(err);
                });
            });
            $addAccount.before($item);
        }
    });

    document.querySelectorAll('.nav-accounts-header').forEach(el => el.classList.remove('d-none'));
}

// Single delegated logout listener — re-entrant paint must not stack per-
// element handlers, and a cross-tab token swap should repaint rather than
// log out the account this tab last painted.
document.addEventListener('click', e => {
    const $logout = (e.target as Element).closest('.logout');
    if (!$logout) {
        return;
    }
    e.preventDefault();
    // Another tab may have swapped accounts since this page painted —
    // logging out then would clear the NEW account's session while
    // evicting the old one from the stored list. (No storage-event reload
    // on purpose: a spontaneous reload could destroy in-progress form
    // input in this tab.)
    if (session.GetLoginToken() !== currentToken) {
        paintSession();
        return;
    }
    const userID = currentUserID;
    if (userID) {
        accounts.Remove(userID);
    }
    session.DeleteSession();
    window.location.reload();
});

// Cross-tab soft refresh: other tabs write loginToken on switch; repaint
// the session header in place so this tab shows the new account without
// destroying in-progress form input. Storage events only fire in OTHER
// tabs — the switcher tab still reloads via onSwitch.
window.addEventListener('storage', e => {
    if (e.key === 'loginToken' && e.newValue !== currentToken) {
        paintSession();
    }
});

function initCart() {
    if (carts.GetCartID()) {
        void carts.GetCartCount().then(count => {
            if (!count) {
                return;
            }

            carts.IncrementCartCount(count);
        });
    }
}

async function main() {
    await session.ConsumeLoginTokenFromURL();
    // Page tracking starts after the exchange so the page request belongs to the
    // account the link signed in, not whoever was signed in before.
    void GetPageRequestID();

    if (QueryString().get('paypal') === 'success') {
        const successAlert = document.querySelector('#paypal-success') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    if (QueryString().get('paypal') === 'error') {
        const successAlert = document.querySelector('#paypal-error') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    const paypalRetryURL = paypal.DeclinedRetryURL();
    if (paypalRetryURL) {
        const declinedAlert = document.querySelector('#paypal-declined') as HTMLElement;
        (declinedAlert.querySelector('#paypal-declined-retry') as HTMLAnchorElement).href = paypalRetryURL;
        declinedAlert.classList.remove('d-none');
        declinedAlert.addEventListener('closed.bs.alert', () => {
            declinedAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    if (QueryString().get('deleteCart')) {
        void carts.DeleteCart();
        QueryString().delete('deleteCart');
    }

    // Sidebar to show on large screens. Listen for the breakpoint instead of
    // checking once — a page loaded narrow never got `show`, so growing the
    // window past lg revealed the wrapper with the collapse still collapsed.
    const accountSidebar = document.querySelector('#account-links') as HTMLElement;
    if (accountSidebar) {
        const lgQuery = window.matchMedia('(min-width: 992px)');
        if (lgQuery.matches) {
            accountSidebar.classList.add('show');
        }
        lgQuery.addEventListener('change', e => {
            if (e.matches) {
                accountSidebar.classList.add('show');
            }
        });
    }

    paintSession();
    initCart();

    // Initialize Meta (Facebook) pixel tracking
    if (window.metaPixel) {
        const pixelId = window.metaPixel;
        void session.Session().then(sess => {
            meta.InitializeMeta(pixelId, sess ? {
                email: sess.user.emailAddress.email,
                phone: sess.user.phoneNumber.phone,
                userId: sess.user.id,
            } : undefined);
        }).catch(() => {
            meta.InitializeMeta(pixelId);
        }).finally(() => {
            meta.TrackPageView();
        });
    }
}

void main();
